import { queryOptions, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import type { Permission } from '@squadcore/api/shared/permissions'
import { useCallback, useSyncExternalStore } from 'react'
import { api, unwrap } from './api'

export const sessionQuery = queryOptions({
  queryKey: ['session'],
  queryFn: () => unwrap(api.session.$get()),
  staleTime: 5 * 60_000,
})

export type Session = Awaited<ReturnType<NonNullable<typeof sessionQuery.queryFn>>>
export type ActiveOrg = NonNullable<Session['active']>

export function useSession(): Session {
  return useSuspenseQuery(sessionQuery).data
}

/** Active organization; only call below the _app layout, which guarantees one exists. */
export function useActive(): ActiveOrg {
  const s = useSession()
  if (!s.active) throw new Error('useActive outside an organization')
  return s.active
}

export function useCan() {
  const { permissions } = useActive()
  return useCallback((p: Permission) => permissions.includes(p), [permissions])
}

// --- Team and season selection: per organization, remembered in localStorage ----------------

const listeners = new Set<() => void>()
const read = (key: string) => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}
const write = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // private mode: selection just isn't remembered
  }
  listeners.forEach((l) => l())
}
const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

const teamKey = (orgId: string) => `sc-team:${orgId}`
const seasonKey = (orgId: string) => `sc-season:${orgId}`

/** Team and season selection outside React (route loaders); useScope() is the hook version. */
export function scopeOf(active: ActiveOrg) {
  const orgId = active.organization.id
  const team = active.teams.find((t) => t.id === read(teamKey(orgId))) ?? active.teams[0] ?? null
  const season = active.seasons.find((s) => s.key === read(seasonKey(orgId)))?.key ?? active.currentSeason
  return {
    organizationId: orgId,
    teamId: team?.id ?? '',
    season,
    currentSeason: active.currentSeason,
    timezone: active.organization.timezone,
    seasonStartMonth: active.organization.seasonStartMonth,
    permissions: active.permissions,
  }
}

/** scopeOf for loaders below _app (which guarantees an active organization). */
export async function loaderScope(qc: QueryClient) {
  const session = await qc.ensureQueryData(sessionQuery)
  if (!session.active) throw new Error('loaderScope outside an organization')
  return scopeOf(session.active)
}

/**
 * The team and season every team-bound screen works on. Team and season are part of every query
 * key, so switching refetches exactly the affected data.
 */
export function useScope() {
  const active = useActive()
  const tKey = teamKey(active.organization.id)
  const sKey = seasonKey(active.organization.id)
  const storedTeam = useSyncExternalStore(subscribe, () => read(tKey))
  const storedSeason = useSyncExternalStore(subscribe, () => read(sKey))
  const team = active.teams.find((t) => t.id === storedTeam) ?? active.teams[0] ?? null
  const season =
    active.seasons.find((s) => s.key === storedSeason)?.key ?? active.currentSeason
  return {
    org: active.organization,
    team,
    teamId: team?.id ?? '',
    teams: active.teams,
    season,
    seasons: active.seasons,
    isCurrentSeason: season === active.currentSeason,
    isArchive: season !== active.currentSeason,
    setTeam: (id: string) => write(tKey, id),
    setSeason: (key: string) => write(sKey, key === active.currentSeason ? null : key),
  }
}

export function useRefreshSession() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ['session'] })
}
